import { Link, useNavigate } from 'react-router-dom';

import { api } from '../../api/endpoints.js';
import { Page } from '../../components/AppShell.jsx';
import { Badge, Card, DataTable, Input, Pagination, Select } from '../../components/ui.jsx';
import { useListQuery } from '../../hooks/useListQuery.js';
import { dateNumeric, initials, number, phoneWithCountryCode, relative } from '../../lib/format.js';

/**
 * Customer accounts: the people who rent and the people who let their own
 * home.
 *
 * Agencies are not here. They are a separate collection — the same person can
 * hold a customer account and a broker one, and their histories are meant to
 * stay apart — and they have their own screen, where the things worth knowing
 * about an agency are the things on it.
 */
export default function UsersPage() {
  const navigate = useNavigate();

  const list = useListQuery({
    key: 'admin-users',
    fetcher: api.users.list,
    initialFilters: { status: '', identity: '', deleted: '', visibility: 'visible' },
    searchable: true,
  });

  const columns = [
    {
      key: 'person',
      header: 'Person',
      render: (user) => (
        <div className="row">
          <div
            className="sidebar__avatar"
            style={{ background: 'var(--surface-container)', color: 'var(--primary)' }}
          >
            {initials(user.name)}
          </div>
          <div>
            <Link className="table__link table__primary" to={`/users/${user._id}`}>
              {user.name || 'No name yet'}
            </Link>
            <div className="table__muted mono">{phoneWithCountryCode(user.phone)}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'setup',
      header: 'Set up',
      render: (user) =>
        user.profileCompletedAt ? (
          <span className="table__muted">{dateNumeric(user.profileCompletedAt)}</span>
        ) : (
          <Badge tone="warning" label="Not finished" />
        ),
    },
    {
      key: 'credits',
      header: 'Credits',
      className: 'table__numeric',
      render: (user) => number(user.creditBalance || 0),
    },
    {
      key: 'status',
      header: 'Status',
      render: (user) => (
        <div className="row">
          <Badge status={user.status} />
          {user.isTestAccount ? <Badge tone="neutral" label="Test" /> : null}
          {user.hiddenFromPublic ? <Badge tone="warning" label="Hidden" /> : null}
        </div>
      ),
    },
    {
      key: 'joined',
      header: 'Joined',
      render: (user) => <span className="table__muted">{dateNumeric(user.createdAt)}</span>,
    },
    {
      key: 'seen',
      header: 'Last seen',
      render: (user) => (
        <span className="table__muted">{user.lastLoginAt ? relative(user.lastLoginAt) : 'Never'}</span>
      ),
    },
  ];

  return (
    <Page title="Users" subtitle="Tenants, and owners letting their own home.">
      <Card bodyless>
        <div className="card__body">
          <div className="toolbar">
            <div className="toolbar__search">
              <Input
                placeholder="Search by name or phone"
                value={list.search}
                onChange={(event) => list.setSearch(event.target.value)}
                aria-label="Search users"
              />
            </div>

            <Select
              value={list.filters.status}
              onChange={(event) => list.setFilter('status', event.target.value)}
              aria-label="Filter by status"
            >
              <option value="">Any status</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="deactivated">Deactivated</option>
            </Select>

            <Select
              value={list.filters.identity}
              onChange={(event) => list.setFilter('identity', event.target.value)}
              aria-label="Filter by identity check"
            >
              <option value="">Any identity check</option>
              <option value="pending">ID waiting for review</option>
              <option value="verified">ID verified</option>
              <option value="rejected">ID rejected</option>
            </Select>

            <Select
              value={list.filters.deleted}
              onChange={(event) => list.setFilter('deleted', event.target.value)}
              aria-label="Live or deleted accounts"
            >
              <option value="">Live accounts</option>
              <option value="true">Deleted accounts</option>
            </Select>

            <Select
              value={list.filters.visibility}
              onChange={(event) => list.setFilter('visibility', event.target.value)}
              aria-label="Visible or hidden from real users"
            >
              <option value="visible">Visible to real users</option>
              <option value="hidden">Hidden from real users</option>
              <option value="all">Visible and hidden</option>
            </Select>
          </div>
        </div>

        <DataTable
          columns={columns}
          rows={list.rows}
          query={list.query}
          onRowClick={(user) => navigate(`/users/${user._id}`)}
          empty={{
            title: 'Nobody here',
            body: 'No account matches these filters. Clear them if you expected to see one.',
          }}
        />
        <Pagination meta={list.meta} page={list.page} onPageChange={list.setPage} />
      </Card>
    </Page>
  );
}
